import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { FieldmagicConfiguredIdentifiableDropdownOption } from '../shared/components/forms/input/dropdown/dropdown.component';
import { arr_map, arr_wrap, fallback, filled, map_to_arr, spf } from '../shared/utils/common';

@Injectable()
export class FieldmagicDataGridDropdownService {
  private readonly _cache = new Map<string, _CachedDropdownDetails>();

  constructor(
    private readonly _api: ApiService,
  ) {}

  getOptions(opts: {
    module_name: string;
    field_name: string;
  }): Observable<FieldmagicDataGridConfiguredDropdownOption[]> {
    const cacheKey = spf('%s:%s', {
      args: [opts.module_name, opts.field_name],
    });

    const cached = this._cache.get(cacheKey);

    if (filled(cached)
      && cached.expires_in > Date.now()
    ) {
      return cached.inflight$;
    }

    const request$ = this._api.callPrivate$({
      path: '/dropdown/field_options',
      body: {
        field: opts.field_name,
        module: opts.module_name,
      },
      onSuccessResponse: (response) => arr_map(fallback(map_to_arr(response), {
        to: () => [],
      }), {
        transformer: (value) => ({
          id: value.key,
          text: value.name,
          color: value.color,
        }),
      }),
    });

    this._cache.set(cacheKey, {
      expires_in: Date.now() + _RELATE_CACHE_TTL,
      inflight$: request$,
    });

    return request$;
  }

  addOptions(opts: {
    module_name: string;
    field_name: string;
    options: FieldmagicDataGridDropdownData[] | FieldmagicDataGridDropdownData,
  }): void {
    this._api.callPrivate$({
      path: '/dropdown/save',
      body: {
        module: opts.module_name,
        field: opts.field_name,
        options: arr_map(arr_wrap(opts.options), {
          transformer: (option: FieldmagicDataGridDropdownData) => ({
            key: option.id,
            name: option.text,
            position: fallback(option.position, {
              to: () => 0,
            }),
            color: fallback(option.color, {
              to: () => '#868e96',
            }),
          }),
        }),
      },
      onSuccessResponse: () => true,
    }).subscribe();

    return;
  }
}

export type FieldmagicDataGridConfiguredDropdownOption = FieldmagicConfiguredIdentifiableDropdownOption;
export type FieldmagicDataGridDropdownData = {
  id: string;
  text: string;
  position?: number;
  color?: string;
};

type _CachedDropdownDetails = {
  inflight$: Observable<FieldmagicConfiguredIdentifiableDropdownOption[]>;
  expires_in: number;
};

const _RELATE_CACHE_TTL = 5 * 60 * 1000; /// 5 minutes;